import { css } from '@emotion/react'
import { graphql } from 'gatsby'
import {
  ComponentPropsWithoutRef,
  Dispatch,
  SetStateAction,
  useEffect,
  useState,
} from 'react'
import { useInView } from 'react-intersection-observer'

import { GatsbyImageFocused } from '@/features/common/'
import { useElementHeight } from '@/hooks/useElementRect'
import { absoluteFill, bezier, scrim } from '@/theme/mixins'
import { colors } from '@/theme/variables'

import type { MediaCarouselLayout } from './MediaCarousel'

interface Props extends ComponentPropsWithoutRef<'figure'> {
  data?: Queries.MediaCarouselImageFragment | null
  carouselHeight: number | null
  setCarouselHeight?: Dispatch<SetStateAction<number | null>>
  layout: MediaCarouselLayout
}

export const MediaCarouselImage = ({
  data,
  carouselHeight,
  setCarouselHeight,
  layout,
  ...props
}: Props) => {
  const { image } = data || {}
  const { inView, ref: inViewRef } = useInView({
    rootMargin: '20% -50%',
  })
  const [heightRef, setCarouselHeightRef] =
    useState<HTMLElement | null>(null)
  const height = useElementHeight(heightRef)

  useEffect(() => {
    if (setCarouselHeight && inView && height) {
      setCarouselHeight(height)
    }
  }, [inView, height, setCarouselHeight])

  const getImageData = () => {
    switch (layout) {
      case 'ARTICLE':
        return image?.uncropped
      case 'HIGHLIGHT':
        return image?.highlight
    }
  }

  const styles = {
    figure: css`
      width: 100%;
      position: relative;
      margin: var(--border-thickness) 0;
      align-self: flex-start;
      ${layout === 'ARTICLE' &&
      css`
        &:before {
          content: '';
          position: absolute;
          top: calc(-1 * var(--border-thickness));
          left: calc(-1 * var(--border-thickness));
          width: calc(100% + 2 * var(--border-thickness));
          height: calc(100% + 2 * var(--border-thickness));
          background: ${colors.gray95};
        }
        &:after {
          content: '';
          position: absolute;
          height: 1rem;
          width: 100%;
          background: ${colors.gray95};
          top: ${carouselHeight &&
          height &&
          Math.min(carouselHeight, height)}px;
          left: 0;
          transition: top 750ms ease;
        }
      `}
      ${layout === 'HIGHLIGHT' &&
      css`
        display: grid;
        height: 100%;
      `}
    `,
    opacityWrapper: css`
      position: relative;
      opacity: 0.125;
      transition: opacity 500ms ${bezier.easeOut};
      pointer-events: none;
      display: grid;
      ${inView &&
      css`
        opacity: 1;
        transition-duration: 1000ms;
        pointer-events: all;
        &:after {
          opacity: 0;
        }
      `}
      ${layout === 'HIGHLIGHT' &&
      css`
        display: grid;
      `}
    `,
    image: css`
      grid-area: 1 / 1 / 2 / 2;
      [data-class-note] & {
        max-height: calc(
          100lvh - var(--nav-height-min, 0px) - var(--cnn-height, 0px) - 6rem
        );
        --object-fit: contain;
        background-color: #00000011;
      }
    `,
    figcaption: css`
      position: relative;
      font-size: var(--fs-15);
      font-style: italic;
      color: ${colors.gray20};
      margin: 1rem 0 0;
      max-width: 108ch;
      transition: opacity 300ms ease;
      opacity: ${inView ? 1 : 0};
      ${layout === 'HIGHLIGHT' &&
      css`
        grid-area: 1 / 1 / 2 / 2;
        align-self: flex-end;
        padding: 4em 1em 2rem calc(9.25rem + 1em);
        position: relative;
        > span {
          position: relative;
          color: #fff;
        }
        &:before {
          content: '';
          ${absoluteFill};
          background: ${scrim('#000', 0.75, 'to top')};
        }
      `}
    `,
  }

  return (
    <figure
      css={styles.figure}
      ref={node => {
        setCarouselHeightRef(node)
        inViewRef(node)
      }}
      {...props}
    >
      <div css={styles.opacityWrapper}>
        <GatsbyImageFocused
          image={getImageData()}
          data={image}
          css={styles.image}
        />
        {image?.title && (
          <figcaption css={styles.figcaption}>
            <span>{image.title}</span>
          </figcaption>
        )}
      </div>
    </figure>
  )
}

export const MediaCarouselImageFragment = graphql`
  fragment MediaCarouselImage on DatoCmsImageBlock {
    __typename
    id: originalId
    image {
      uncropped: gatsbyImageData(width: 1280)
      # wide: gatsbyImageData(
      #   width: 1280
      #   imgixParams: { ar: "3:2", fit: "crop", crop: "focalpoint" }
      # )
      highlight: gatsbyImageData(
        width: 720
        imgixParams: {
          q: 75
          ar: "4:3"
          fit: "crop"
          crop: "focalpoint"
        }
      )
      title
      alt
      width
      height
      ...ImageFocalData
    }
  }
`
